// src/components/block/liveStreamManagement/useHlsProber.ts
// AJOUT : paramètre onStatusChange — appelé à chaque changement de statut.
// Quand une source HLS passe en 'failed', LiveStreamManager peut réagir
// automatiquement (Power Off de la source).
// v2 : startProbe / retryProbe / _runProbe acceptent options?: ProbeHlsOptions
//      pour propager skipRange aux sources Xtream (évite le 407 sur Range).
// v3 : option `persistent` — sources dont l'absence est un état NORMAL
//      (caméra RTMP, flux « OBS, RTMP... » de la chaîne). Cf. plus bas.

import { useState, useRef, useCallback } from 'react';
import { probeHls, ProbeHlsOptions } from '../../../dataTypes/liveStreamManagment/utils_hls';

export type HlsProbeStatus = 'probing' | 'ok' | 'failed';

export interface ProbeOptions extends ProbeHlsOptions {
  /**
   * Source dont l'absence n'est pas une panne : une caméra RTMP n'existe que
   * pendant que son encodeur publie, et l'ordre normal des choses est de la
   * déclarer AVANT. Une telle source garde le régime « première connexion »
   * même après avoir déjà répondu, et n'est jamais abandonnée : la sonde
   * continue en fond, lentement, jusqu'à ce qu'elle revienne.
   * Mémorisé par sourceId : les appels qui ne le repassent pas (rallumage
   * manuel du slot) conservent le régime déjà connu pour cette source.
   */
  persistent?: boolean;
}

// Deux régimes, parce que les deux situations n'ont rien à voir.
//
// PREMIÈRE CONNEXION — la source n'a jamais répondu de la session. La chaîne
// peut être en train de démarrer (activation, premier segment HLS pas encore
// écrit) : l'attente est légitime et il faut être patient. 15 × 8 s = 120 s.
//
// RECONNEXION — la source répondait il y a un instant. Un échec n'est donc PAS
// une absence, c'est un hoquet (bufferStalledError, 401 sur une sous-playlist,
// segment manquant). Rester 2 minutes en 'probing' y est une faute : le slot est
// inerte tout ce temps, alors qu'en régie en direct il faut rendre la main tout
// de suite. 4 × 1,5 s ≈ 6 s, puis 'failed' — donc un slot de nouveau cliquable.
//
// Constaté le 2026-08-23 : 7 bufferStalledError en 1 minute sur
// `hls-video-<uuid>` et le bouton Playlist inerte, sans autre retour que sa
// couleur (cf. analyse-pipeline-nasa-tv-2026-08-23.md).
//
// SAUF pour une source `persistent` (2026-08-28). Le régime court y était une
// faute symétrique : une caméra RTMP qui a publié une fois est marquée « déjà
// vue » pour toute la session, donc à la coupure suivante le cadreur n'avait
// que 6 s pour relancer son encodeur avant que le slot ne s'éteigne. Ces
// sources-là gardent donc le régime long, puis passent en veille (ci-dessous).
const FIRST_MAX_RETRIES     = 15;
const FIRST_RETRY_DELAY     = 8_000;
const RECONNECT_MAX_RETRIES = 4;
const RECONNECT_RETRY_DELAY = 1_500;

// VEILLE — une source `persistent` qui a épuisé ses tentatives passe bien en
// 'failed' (le slot redevient cliquable, l'opérateur voit qu'il n'y a rien),
// mais la sonde ne s'arrête pas pour autant : elle repasse toutes les 15 s,
// indéfiniment, et le slot repart tout seul dès que l'encodeur publie.
// Un Power Off manuel, une suppression de source ou un forceStatus coupent
// cette veille — ils invalident la génération de sonde (cf. _cancelTimer).
const PERSISTENT_IDLE_DELAY = 15_000;

export function useHlsProber(
  onStatusChange?: (sourceId: string, status: HlsProbeStatus, persistent: boolean) => void,
) {
  const [statusMap, setStatusMap] = useState<Map<string, HlsProbeStatus>>(new Map());
  const statusRef  = useRef<Map<string, HlsProbeStatus>>(new Map());
  const timersRef  = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Sources ayant répondu au moins une fois dans la session : c'est ce qui
  // distingue une première connexion d'une reconnexion (cf. les deux régimes
  // ci-dessus). Volontairement jamais purgé — une source qui a marché une fois
  // reste, pour toute la session, une source dont on sait qu'elle existe.
  const everOkRef  = useRef<Set<string>>(new Set());

  // Sources déclarées `persistent`, mémorisées par identifiant.
  const persistentRef = useRef<Set<string>>(new Set());

  // Génération de sonde par source. Une boucle de sonde ne vaut que tant que sa
  // génération est la courante : sans ça, un `tryOnce` déjà parti (le fetch dure)
  // replanifierait après un forceStatus, et une source supprimée continuerait
  // d'être interrogée toutes les 15 s pour le reste de la session.
  const genRef = useRef<Map<string, number>>(new Map());

  // Ref stable pour le callback — évite de recréer _runProbe à chaque render
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const _cancelTimer = useCallback((sourceId: string) => {
    const t = timersRef.current.get(sourceId);
    if (t !== undefined) { clearTimeout(t); timersRef.current.delete(sourceId); }
    genRef.current.set(sourceId, (genRef.current.get(sourceId) ?? 0) + 1);
  }, []);

  const _setStatus = useCallback((sourceId: string, status: HlsProbeStatus) => {
    if (statusRef.current.get(sourceId) === status) return;
    if (status === 'ok') everOkRef.current.add(sourceId);
    statusRef.current.set(sourceId, status);
    setStatusMap(prev => {
      const next = new Map(prev);
      next.set(sourceId, status);
      return next;
    });
    onStatusChangeRef.current?.(sourceId, status, persistentRef.current.has(sourceId));
  }, []);

  const _runProbe = useCallback((
    sourceId : string,
    url      : string,
    options? : ProbeOptions,
  ) => {
    if (options?.persistent !== undefined) {
      if (options.persistent) persistentRef.current.add(sourceId);
      else                    persistentRef.current.delete(sourceId);
    }
    const persistent = persistentRef.current.has(sourceId);

    _cancelTimer(sourceId);
    const gen = genRef.current.get(sourceId);
    _setStatus(sourceId, 'probing');

    const reconnect  = !persistent && everOkRef.current.has(sourceId);
    const maxRetries = reconnect ? RECONNECT_MAX_RETRIES : FIRST_MAX_RETRIES;
    const retryDelay = reconnect ? RECONNECT_RETRY_DELAY : FIRST_RETRY_DELAY;

    let attempt = 0;

    const tryOnce = async () => {
      if (genRef.current.get(sourceId) !== gen) return;
      const ok = await probeHls(url, options).catch(() => false);
      if (genRef.current.get(sourceId) !== gen) return;
      if (ok) { _setStatus(sourceId, 'ok'); return; }
      attempt++;
      // Tentatives épuisées : abandon pour une source ordinaire, veille lente
      // pour une source persistante — le statut passe à 'failed' dans les deux
      // cas, mais la seconde continue d'être interrogée.
      if (attempt >= maxRetries) {
        _setStatus(sourceId, 'failed');
        if (!persistent) return;
      }
      const t = setTimeout(tryOnce, attempt >= maxRetries ? PERSISTENT_IDLE_DELAY : retryDelay);
      timersRef.current.set(sourceId, t);
    };

    tryOnce();
  }, [_cancelTimer, _setStatus]);

  const startProbe = useCallback((
    sourceId : string,
    url      : string,
    options? : ProbeOptions,
  ) => {
    const current = statusRef.current.get(sourceId);
    if (current === 'ok' || current === 'probing') return;
    _runProbe(sourceId, url, options);
  }, [_runProbe]);

  const retryProbe = useCallback((
    sourceId : string,
    url      : string,
    options? : ProbeOptions,
  ) => {
    _runProbe(sourceId, url, options);
  }, [_runProbe]);

  /**
   * Test UNIQUE, à la demande (clic de l'opérateur), sans boucle ni veille.
   *
   * Pour une source dont l'absence est l'état normal (le flux « OBS, RTMP... » d'une
   * chaîne, le plus souvent hors ligne) : la sonder en permanence ne sert à rien tant
   * qu'aucun bouton ne la porte. Un succès pose le statut 'ok' (la mise à l'antenne
   * peut suivre) ; un échec ne change RIEN — pas de statut 'failed', donc pas de
   * « déconnexion » journalisée pour une source qui n'a jamais été connectée.
   */
  const probeNow = useCallback(async (
    sourceId : string,
    url      : string,
    options? : ProbeOptions,
  ): Promise<boolean> => {
    if (options?.persistent) persistentRef.current.add(sourceId);
    const ok = await probeHls(url, options).catch(() => false);
    if (ok) _setStatus(sourceId, 'ok');
    return ok;
  }, [_setStatus]);

  const getStatus = useCallback((sourceId: string): HlsProbeStatus | undefined => {
    return statusRef.current.get(sourceId);
  }, []);

  // Forcer un statut manuellement sans relancer de probe.
  // Coupe aussi la veille d'une source persistante : c'est par là que passent le
  // Power Off manuel et la suppression d'une source.
  const forceStatus = useCallback((sourceId: string, status: HlsProbeStatus) => {
    _cancelTimer(sourceId);
    _setStatus(sourceId, status);
  }, [_cancelTimer, _setStatus]);

  /** La source a-t-elle déjà répondu dans cette session ? (première connexion vs reconnexion) */
  const wasEverOk = useCallback((sourceId: string): boolean => everOkRef.current.has(sourceId), []);

  /** La source est-elle de celles dont l'absence est normale ? (caméra RTMP, flux OBS) */
  const isPersistent = useCallback((sourceId: string): boolean => persistentRef.current.has(sourceId), []);

  return { statusMap, startProbe, retryProbe, probeNow, getStatus, forceStatus, wasEverOk, isPersistent };
}
